/**
 * 汎用スライダ行 + Shuffle ボタン。
 *
 * 設計書 FR-09 / FR-10 / FR-21 / FR-22 / FR-28〜FR-34 / 10.2。v1.1 で
 * `distribution` は撤廃され、spec §6 の数値フィールド全般をスライダで
 * 編集する構成に拡張されている（layout / shape jitter / fragmentation /
 * explosion motion / text 各 block）。
 *
 * wireframe `wireframes/v0.10/wireframe.html` の `.row > label +
 * input[type=range] + .value` 構造を踏襲し、プロトタイプと視覚的に等価な
 * 出力を保つ。
 */

import type { AppState, Store } from '../../state';

/**
 * `AppState.breath` の型を UI 層で明示的に参照するための alias。
 * state/index.ts の barrel に `BreathParams` が生えていないため、
 * `AppState['breath']` を indexed access で引き出して同等の型を得る。
 * state 層の公開面を触らずに nested slider の型安全を保つための回避策。
 */
type BreathParams = AppState['breath'];

/**
 * `breath` sub-field のキー文字列。`AppState.breath: BreathParams` の
 * 各 number プロパティを dot 付きパスで表現する。Phase D で追加。
 *
 * `mountSlider` 内部で `'breath.'` prefix を検出し、`store.set` を
 * nested update（`breath` object の shallow clone + 差し替え）に展開する。
 * 採用理由: 既存 `mountSlider` API を維持したまま最小コードで対応でき、
 * 呼び出し側は `field: 'breath.pulseAmp'` と書くだけで済む。new helper を
 * 作るより import graph がシンプル（UI 層の binding 方針に合致）。
 */
export type BreathSliderField = 'breath.pulseAmp' | 'breath.pulsePeriod';

/**
 * スライダが書き込める数値フィールド。`AppState` の number 型フィールドの
 * うち、UI で連続値として編集するもの全て。
 *
 * spec §6 のブロック順（layout → shape → fragmentation → explosion →
 * text）で列挙している。`vertexCount` は離散ピッカ（polygonPicker）で
 * 扱うためここには含めない（Q5 確定）。
 *
 * Phase D: `breath.*` の dot 付きパスを `BreathSliderField` 経由で受け付ける。
 * 内部で nested update に展開される（`mountSlider` 実装参照）。
 */
export type SliderField =
  // layout
  | 'gutterPct'
  | 'bleedMult'
  | 'textMarginPct'
  // shape
  | 'shapeRandomness'
  | 'shapeScale'
  // fragmentation — Phase J（F）: randomness / granularity は UI 撤去
  | 'fragmentCount'
  // explosion motion
  | 'innerDispPct'
  | 'perimMult'
  | 'speedVariance'
  // text
  | 'fontSizePct'
  | 'tracking'
  | 'followSilhouette'
  // breath (nested: AppState.breath.*)
  | BreathSliderField;

/** `mountSlider` のオプション。 */
export interface SliderOptions {
  /** 行頭に出す日本語 + 英語併記ラベル（例: `"破片数 Count"`）。 */
  label: string;
  /** `AppState` のどのフィールドに書き込むか。 */
  field: SliderField;
  /** `<input type="range" min>` の値。 */
  min: number;
  /** `<input type="range" max>` の値。 */
  max: number;
  /** `<input type="range" step>` の値。 */
  step: number;
  /**
   * store 値 → 画面表示文字列。省略時は `String(value)`。
   *
   * UI の range input raw 値と store のスケールが同じでも、表示上の整形
   * （`(v).toFixed(1)` など）にも使える。スケールが違う場合は本関数に
   * 入るのは **store 値**（例: `fontSizePct` の 3.5）であることに注意。
   */
  displayTransform?: (storeValue: number) => string;
  /**
   * range input raw 値 → store 書き込み値。省略時は identity。
   *
   * store とレンジが異なるスケール（例: UI 側 10..240 整数 / store 側
   * 1.0..24.0 浮動小数）のとき、`storeTransform` と `rangeTransform` の
   * **ペア**で双方向マッピングを作る。片方だけ指定すると逆変換時に
   * ズレるので、スケール変換時は必ず両方指定すること。
   */
  storeTransform?: (rangeValue: number) => number;
  /**
   * store 値 → range input raw 値。省略時は identity。
   * `storeTransform` の逆関数。初期化時と外部からの store 変更追従時に
   * range input の `value` を再構築するのに使う。
   */
  rangeTransform?: (storeValue: number) => number;
}

/**
 * スライダ行をマウントする。
 *
 * @param root - セクションのコンテナ
 * @param store - グローバルストア
 * @param options - label / field / min / max / step
 * @returns teardown 関数
 */
export function mountSlider(
  root: HTMLElement,
  store: Store<AppState>,
  options: SliderOptions,
): () => void {
  // identity 既定値。オプション省略時の後方互換のため。
  const toDisplay = options.displayTransform ?? ((v: number): string => String(v));
  const toStore = options.storeTransform ?? ((v: number): number => v);
  const toRange = options.rangeTransform ?? ((v: number): number => v);

  // Phase D: `breath.*` は nested update。dot 付きパスを検出して
  // 読み書きのアクセサを分岐する（API 互換性のためフラットパスは従来通り）。
  const breathKey: keyof BreathParams | null = options.field.startsWith('breath.')
    ? (options.field.slice('breath.'.length) as keyof BreathParams)
    : null;

  /** 現在の store 値を数値で読む（flat / nested 両対応）。 */
  const readStore = (state: AppState): number => {
    if (breathKey !== null) return state.breath[breathKey];
    return state[options.field as Exclude<SliderField, BreathSliderField>] as number;
  };

  /** store 値を書き込む。nested の場合は `breath` object を shallow clone。 */
  const writeStore = (v: number): void => {
    if (breathKey !== null) {
      const current = store.get().breath;
      store.set({ breath: { ...current, [breathKey]: v } });
      return;
    }
    store.set({ [options.field]: v } as Partial<AppState>);
  };

  const row = document.createElement('div');
  row.className = 'row';

  const label = document.createElement('label');
  label.textContent = options.label;
  row.appendChild(label);

  const input = document.createElement('input');
  input.type = 'range';
  input.min = String(options.min);
  input.max = String(options.max);
  input.step = String(options.step);
  const initialStore = readStore(store.get());
  input.value = String(toRange(initialStore));
  row.appendChild(input);

  const value = document.createElement('div');
  value.className = 'value';
  value.textContent = toDisplay(initialStore);
  row.appendChild(value);

  input.addEventListener('input', (e) => {
    const raw = (e.target as HTMLInputElement).value;
    const n = Number(raw);
    // NaN ガード。空文字等が来た場合は無視してユーザの次入力に任せる。
    if (!Number.isFinite(n)) return;
    const storeValue = toStore(n);
    value.textContent = toDisplay(storeValue);
    writeStore(storeValue);
  });

  const unsubscribe = store.subscribe((state, prev) => {
    const nextV = readStore(state);
    const prevV = readStore(prev);
    if (nextV !== prevV) {
      const nextRange = String(toRange(nextV));
      // 自分が発火させた set() でも一応書き戻すが、文字列一致チェックで
      // 冗長な DOM 更新を避ける。ドラッグ中のカーソル位置も保てる。
      if (input.value !== nextRange) input.value = nextRange;
      value.textContent = toDisplay(nextV);
    }
  });

  root.appendChild(row);

  return (): void => {
    unsubscribe();
    row.remove();
  };
}

/**
 * Shuffle Seed ボタン（FR-12）。
 *
 * プロトタイプに合わせ、ghost ボタンで `Fragmentation` セクション末尾に置く。
 * クリックで `seed` を `Math.random()` で再抽選する。`↻` は Unicode 幾何記号
 * であり絵文字ではないため、モノトーン原則に抵触しない。
 *
 * @param root - 親コンテナ（通常は Fragmentation セクション）
 * @param store - グローバルストア
 * @returns teardown 関数
 */
export function mountShuffle(root: HTMLElement, store: Store<AppState>): () => void {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn ghost';
  btn.textContent = '↻ Shuffle Seed';
  // プロトタイプの margin-top: 6px をインラインではなく class で持たせる
  btn.classList.add('btn-shuffle');

  const onClick = (): void => {
    store.set({ seed: Math.random() });
  };
  btn.addEventListener('click', onClick);

  root.appendChild(btn);

  return (): void => {
    btn.removeEventListener('click', onClick);
    btn.remove();
  };
}
